import {ENDPOINT_DYNAMIC_DATA} from 'common/endpoints';
import {commonJwtHeader} from 'common/utils';
import _ from 'lodash';

// get grants by organization id from dynamo
const getGrantsByOrganization = (token, orgId, programId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}organization/${orgId}/entityType/grant${
      programId ? `?programIds=${programId}` : ''
    }`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// get applications by organization id from dynamo
const getApplicationsByOrganization = (token, orgId, programId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}organization/${orgId}/entityType/application${
      programId ? `?programIds=${programId}` : ''
    }`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// get status of RFA by suborganization id, organization id, foid from dynamicdata
const getRfaStatusBySubOrganizationOrganizationFo = ({subOrgId, orgId, foId}) => {
  if (_.isUndefined(subOrgId) || _.isUndefined(orgId) || _.isUndefined(foId)) {
    return Promise.reject({message: 'missing arguments'});
  }
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}statusCheck/organization/${orgId}/suborganization/${subOrgId}/fundingOpportunityId/${foId}/entityType/rfa`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// get status of subApps by suborganization id, organization id, foid from dynamicdata
const getSubAppStatusBySubOrganizationOrganizationFo = ({subOrgId, orgId, foId}) => {
  if (_.isUndefined(subOrgId) || _.isUndefined(orgId) || _.isUndefined(foId)) {
    return Promise.reject({message: 'missing arguments'});
  }
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}statusCheck/organization/${orgId}/suborganization/${subOrgId}/fundingOpportunityId/${foId}/entityType/subApplication`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// get status of RFA by organization id, foid from dynamicdata
const getRfaStatusByOrganizationFo = (token, orgId, foId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}statusCheck/organization/${orgId}/fundingOpportunityId/${foId}/entityType/rfa`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

//new endpoint for getting subapplications
const getSubapplicationsByToken = (token) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entityTypes/subApplication`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};
// get subapplications by organization id from dynamo
const getSubapplicationsByOrganization = (token, orgId, programId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}organization/${orgId}/entityType/subApplication${
      programId ? `?programIds=${programId}` : ''
    }`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// get subapplications by organization id and categoryId from dynamo
const getSubapplicationsByOrgAndCategoryId = (token, orgId, categoryId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}organization/${orgId}/categoryId/${categoryId}/entityType/subApplication`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// required because RestApiCallListener only accepts functions using destructured parameters
const callGetEntityById = ({token, entityId}) => {
  return getEntityById(token, entityId);
};

// get entity by id
const getEntityById = (token, entityId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entity/${entityId}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const createApplicationEntity = (token, payload) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}`, {
    method: 'POST',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const updateEntity = (token, entity) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entity/${entity.entityId}`, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(entity),
  });
};

const patchEntity = (token, entity) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entity/${entity.entityId}`, {
    method: 'PATCH',
    headers: commonJwtHeader(),
    body: JSON.stringify(entity),
  });
};

const patchEntitySubmittedToFEMA = ({entity}) => {
  const updatedEntity = {
    ...entity,
    status: 'submittedToFEMA',
  };
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entity/${entity.entityId}`, {
    method: 'PATCH',
    headers: commonJwtHeader(),
    body: JSON.stringify(updatedEntity),
  });
};

const modifySubApp = (token, entity, entityId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}subApplication/${entityId}/modify`, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(entity),
  });
};
const deleteEntity = (token, entityId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entity/${entityId}`, {
    method: 'DELETE',
    headers: commonJwtHeader(),
  });
};

const lock = (token, grantId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}grant/${grantId}/lock`, {
    method: 'PUT',
    headers: commonJwtHeader(),
  });
};

const unlock = (token, grantId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}grant/${grantId}/unlock`, {
    method: 'PUT',
    headers: commonJwtHeader(),
  });
};

const lockEntities = (token, entitiesData) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}lock/`, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(entitiesData),
  });
};

const unlockEntities = (token, entitiesData) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}unlock/`, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(entitiesData),
  });
};

const updateBatchData = (token, payload) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}`, {
    method: 'PATCH',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const callGetAllByGrantIdAndEntityType = ({token, grantId, entityType}) => {
  return getAllByGrantIdAndEntityType(token, grantId, entityType);
};

const callGetAllBySubGrantsAndEntityType = ({token, subGrants, entityType}) => {
  return Promise.all(
    subGrants.map((subGrant) =>
      getAllByGrantIdAndEntityType(token, subGrant.grantId, entityType)
    )
  );
};

const getAllByGrantIdAndEntityType = (token, grantId, entityType) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}grant/${grantId}/entityType/${entityType}/all`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getByGrantIdAndEntityType = (token, grantId, entityType) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}grant/${grantId}/entityType/${entityType}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getBySubGrantIdAndEntityType = (token, subGrantId, entityType) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}subGrant/${subGrantId}/entityType/${entityType}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const callGetBySubGrantIdAndEntityType = ({token, subGrantId, entityType}) => {
  return getBySubGrantIdAndEntityType(token, subGrantId, entityType);
};

const createEntity = (token, grantId, entityType) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}grant/${grantId}/entityType/${entityType}`, {
    method: 'POST',
    headers: commonJwtHeader(),
  });
};

const createEntityFromSubGrant = (token, subGrantId, entityType) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}subGrant/${subGrantId}/entityType/${entityType}`,
    {
      method: 'POST',
      headers: commonJwtHeader(),
    }
  );
};

const createGrant = (token, payload) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}grant`, {
    method: 'POST',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const updateApplicationLinkEntities = (jwt_token, payload) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}applicationLink`, {
    method: 'POST',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const deleteApplicationLinkEntity = (jwt_token, applicationLinkEntityId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}applicationLink/entity/${applicationLinkEntityId}`,
    {
      method: 'DELETE',
      headers: commonJwtHeader(),
    }
  );
};

const getEntitiesBySubGrantIdAndEntityType = (subGrantId, entityType) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}subGrant/${subGrantId}/entityType/${entityType}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const deleteMultipleApplicationLinks = (
  jwt_token,
  applicationEntityId,
  applicationLinkEntities
) => {
  const payload = {
    ids: applicationLinkEntities,
  };
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}applicationLink/entity/${applicationEntityId}/delete`,
    {
      method: 'DELETE',
      headers: commonJwtHeader(),
      body: JSON.stringify(payload),
    }
  );
};

const getSubAmendmentRecommendationsForGrant = (grantId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}grant/${grantId}/entityType/subAmendmentRecommendation/all`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const getSubEntityByGrantIdTypeAndSubGrantId = ({grantId, entityType, subGrantId}) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}grant/${grantId}/entityType/${entityType}/subGrant/${subGrantId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const getEntityByOrgIdAndFOId = (organizationId, fundingOpportunityId, entityType) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}organization/${organizationId}/fundingOpportunityId/${fundingOpportunityId}/entityType/${entityType}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const callGetEntityByOrgIdAndFOId = ({
  organizationId,
  fundingOpportunityId,
  entityType,
}) => {
  return getEntityByOrgIdAndFOId(organizationId, fundingOpportunityId, entityType);
};

const getEntityByEntityTypeAndFOId = (entityType, fundingOpportunityId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}fundingOpportunityId/${fundingOpportunityId}/entityType/${entityType}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const callGrantIdByFOIdAndOrgId = ({organizationId, fundingOpportunityId}) => {
  return grantIdByFOIdAndOrgId(organizationId, fundingOpportunityId);
};

const grantIdByFOIdAndOrgId = (organizationId, fundingOpportunityId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}organization/${organizationId}/fundingOpportunityId/${fundingOpportunityId}/grantId`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const putRiskSectionsToGrant = ({grantId, riskControls}) => {
  const payload = {
    payload: {
      riskControls,
    },
  };

  return fetch(`${ENDPOINT_DYNAMIC_DATA}grant/${grantId}`, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const saveForLater = ({entityId, taskId, entityName, entityDataType}) => {
  const payload = {
    entityId,
    taskId,
    entityName,
    entityDataType,
  };

  return fetch(`${ENDPOINT_DYNAMIC_DATA}saveForLater`, {
    method: 'POST',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const getSavedForUserByEntityId = (entityId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}saveForLater/${entityId}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getSavedForUserByTaskId = (taskId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}saveForLaterTask/${taskId}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getSavedForUser = () => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}saveForLater`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const unsaveForLater = (saveForLaterId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}unsaveForLater/${saveForLaterId}`, {
    method: 'DELETE',
    headers: commonJwtHeader(),
  });
};

export default {
  getGrantsByOrganization,
  getApplicationsByOrganization,
  getRfaStatusBySubOrganizationOrganizationFo,
  getSubAppStatusBySubOrganizationOrganizationFo,
  getRfaStatusByOrganizationFo,
  getSubapplicationsByToken,
  getSubapplicationsByOrganization,
  getSubapplicationsByOrgAndCategoryId,
  getEntitiesBySubGrantIdAndEntityType,
  updateEntity,
  patchEntity,
  patchEntitySubmittedToFEMA,
  modifySubApp,
  deleteEntity,
  updateBatchData,
  getEntityById,
  callGetAllByGrantIdAndEntityType,
  callGetAllBySubGrantsAndEntityType,
  getAllByGrantIdAndEntityType,
  getByGrantIdAndEntityType,
  getBySubGrantIdAndEntityType,
  callGetBySubGrantIdAndEntityType,
  createEntity,
  createEntityFromSubGrant,
  updateApplicationLinkEntities,
  createGrant,
  createApplicationEntity,
  deleteApplicationLinkEntity,
  deleteMultipleApplicationLinks,
  lock,
  unlock,
  lockEntities,
  unlockEntities,
  getSubAmendmentRecommendationsForGrant,
  getSubEntityByGrantIdTypeAndSubGrantId,
  putRiskSectionsToGrant,
  callGetEntityById,
  getEntityByOrgIdAndFOId,
  callGrantIdByFOIdAndOrgId,
  grantIdByFOIdAndOrgId,
  getEntityByEntityTypeAndFOId,
  callGetEntityByOrgIdAndFOId,
  saveForLater,
  getSavedForUserByEntityId,
  getSavedForUserByTaskId,
  getSavedForUser,
  unsaveForLater,
};
